import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Eye, EyeOff, Loader2, Shield, Check } from 'lucide-react';
import { authAPI } from '../../services/adminApi';
import { toast } from 'sonner';

const AdminProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState({ email: '', name: '' });
  const [passwordData, setPasswordData] = useState({ 
    current_password: '', 
    new_password: '', 
    confirm_password: '' 
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const data = await authAPI.getMe();
      setUser(data);
      setProfileData({ email: data.email, name: data.name });
    } catch (error) {
      toast.error('Kullanıcı bilgileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSavingProfile(true);

    try {
      const result = await authAPI.updateProfile(profileData);
      setUser(result.admin);
      toast.success('Profil güncellendi');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalı');
      return;
    }

    setSavingPassword(true);

    try {
      await authAPI.updatePassword(passwordData.current_password, passwordData.new_password);
      toast.success('Şifre güncellendi');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingPassword(false);
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium"
          style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }}>
          <Shield size={14} />
          Admin
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium"
        style={{ background: 'rgba(0, 240, 255, 0.2)', color: '#00F0FF' }}>
        <User size={14} />
        Editor
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin" size={32} style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-profile">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: '#F8FAFC' }}>
          Profil Ayarları
        </h1>
        <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
          Hesap bilgilerinizi ve şifrenizi güncelleyin
        </p>
      </div>

      {/* User Info Card */}
      <div className="rounded-xl p-6" style={{
        background: 'rgba(30, 41, 59, 0.6)',
        border: '1px solid rgba(0, 240, 255, 0.2)'
      }}>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #00F0FF 0%, #0066FF 100%)' }}>
            <User size={32} style={{ color: '#0F172A' }} />
          </div>
          <div>
            <h2 className="text-xl font-semibold" style={{ color: '#F8FAFC' }}>{user?.name}</h2>
            <p className="text-sm" style={{ color: '#94A3B8' }}>{user?.email}</p>
            <div className="mt-2">
              {getRoleBadge(user?.role)}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Form */}
      <div className="rounded-xl p-6" style={{
        background: 'rgba(30, 41, 59, 0.6)',
        border: '1px solid rgba(0, 240, 255, 0.2)'
      }}>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#F8FAFC' }}>
          <Mail size={20} style={{ color: '#00F0FF' }} />
          Profil Bilgileri
        </h3>

        <form onSubmit={handleProfileSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
              İsim
            </label>
            <input
              type="text"
              value={profileData.name}
              onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:ring-2"
              style={{
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(0, 240, 255, 0.3)',
                color: '#F8FAFC'
              }}
              data-testid="profile-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
              E-posta
            </label>
            <input
              type="email"
              value={profileData.email}
              onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-lg outline-none transition-all focus:ring-2"
              style={{
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(0, 240, 255, 0.3)',
                color: '#F8FAFC'
              }}
              data-testid="profile-email-input"
            />
          </div>

          <button
            type="submit"
            disabled={savingProfile}
            className="w-full px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #00F0FF 0%, #0066FF 100%)',
              color: '#0F172A'
            }}
            data-testid="save-profile-btn"
          >
            {savingProfile ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <Check size={18} />
            )}
            Profili Güncelle
          </button>
        </form>
      </div>

      {/* Password Form */}
      <div className="rounded-xl p-6" style={{
        background: 'rgba(30, 41, 59, 0.6)',
        border: '1px solid rgba(0, 240, 255, 0.2)'
      }}>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#F8FAFC' }}>
          <Lock size={20} style={{ color: '#00F0FF' }} />
          Şifre Değiştir
        </h3>

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
              Mevcut Şifre
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg outline-none transition-all pr-12"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: '#94A3B8' }}
              >
                {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
              Yeni Şifre
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                minLength={6}
                className="w-full px-4 py-3 rounded-lg outline-none transition-all pr-12"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: '#94A3B8' }}
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
              Yeni Şifre (Tekrar)
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
                minLength={6}
                className="w-full px-4 py-3 rounded-lg outline-none transition-all pr-12"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: '#94A3B8' }}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={savingPassword}
            className="w-full px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)',
              color: '#0F172A'
            }}
            data-testid="save-password-btn"
          >
            {savingPassword ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <Lock size={18} />
            )}
            Şifreyi Değiştir
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminProfile;
